import { memo } from "react";
import { PanelRightIcon } from "lucide-react";
import { useSpaceIdentity } from "@/api/hooks";
import { useBreakpoint } from "@/common/hooks";
import { Button } from "../button";
import { HeaderText } from "./Header";
import UserAvatar from "./UserAvatar";
import { useCollectionsSidebarToggle } from "./useSidebarToggle";

/**
 * The header's one collections control: whose space this is, and the way into
 * it.
 *
 * It used to carry the route's title — the collection's name on a collection
 * page. That name now heads the list itself, in `RecordsBand`, where it sits
 * against the records it names instead of two components away from them. What
 * is left here is the identity, which is the one thing true of every route and
 * the one thing that says what the sidebar behind the button will contain.
 *
 * The identity is the space being browsed, not the signed-in user: in someone
 * else's space the button opens *their* collections, and your own account
 * already has a home in that sidebar's footer.
 *
 * The identity shows only while the collections sidebar is off screen —
 * collapsed on desktop, or shut on mobile — since the open sidebar carries the
 * same name and picture in its own header. Once it is open on desktop this
 * collapses to a plain trigger, the mirror of the time control on the left.
 * On mobile only the avatar rides in the header; the name would crowd out the
 * interval.
 *
 * Being signed in or not does not enter into it: someone else's space names
 * its owner whether or not you have an account. What is left is the case with
 * no space identity at all — signed out on no one's page — which also falls
 * back to the bare trigger.
 */
function CurrentPageWidget() {
  const { open, toggle } = useCollectionsSidebarToggle();
  const { isMd } = useBreakpoint();
  const { name, picture } = useSpaceIdentity();

  const title = `${open ? "Hide" : "Show"} collections (⌘J)`;

  const showIdentity = !!(name || picture);

  // Nothing to name: the sidebar already carries the name (open on desktop),
  // or there is no space identity — signed out on no one's page, or still
  // resolving whose page this is. Show the bare trigger, the mirror of the
  // time control on the left.
  if ((isMd && open) || !showIdentity) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={toggle}
        title={title}
        className="shrink-0"
        aria-label={title}
      >
        <PanelRightIcon />
      </Button>
    );
  }

  const label = name || "You";
  // On mobile the avatar stands in for the name so the interval keeps its room.
  const showLabel = isMd;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggle}
      title={title}
      // `shrink` overrides the variant's shrink-0: a long name gives way before
      // it pushes the interval off the other end.
      className="min-w-0 shrink gap-2"
    >
      {showLabel && <HeaderText>{label}</HeaderText>}
      {showIdentity && (
        <UserAvatar name={name} picture={picture} className="size-5 shrink-0" />
      )}
    </Button>
  );
}

export default memo(CurrentPageWidget);
